"use client";

// Browser-side SSE consumer for `/api/sse/group`.
//
// ONE EventSource per URL per tab, shared by every hook instance. The
// map page alone mounts four consumers (map view, voice announcer,
// header day/night, header server pill) — as independent EventSources
// they each held their own Valkey subscriber server-side and each
// received + parsed the full envelope stream (observed: 12 users ≈ 50
// subscribers, with bulky marker envelopes fanned out per stream).
// Sharing cuts that 4× to 1× per tab, and the JSON parse happens once
// per message instead of once per consumer.
//
// Why a custom hook over raw EventSource:
//   - Stable typed event shape (RealtimeEvent) instead of `MessageEvent<string>`.
//   - Tracks connection state for the UI (connecting / connected /
//     disconnected) so we can render an indicator.
//   - Handles sse.refresh (server-side visibility change → reconnect)
//     and system.reload (deploy broadcast → conditional page reload)
//     centrally, once per tab.
//
// What it does NOT do:
//   - Buffer events. Consumers that need history must persist their
//     own state. Each event goes to every registered callback once and
//     the most recent is stashed in lastEvent for UI display.

import { useEffect, useRef, useState } from "react";
import type { RealtimeEvent } from "@/lib/realtime/events";
import {
  isStreamStalled,
  reconnectDelayMs,
  SSE_STALL_MS,
  SSE_WATCHDOG_INTERVAL_MS,
} from "@/lib/realtime/sse-reconnect";

export type SSEConnectionState = "idle" | "connecting" | "connected" | "disconnected";

export interface UseSSEReturn {
  state: SSEConnectionState;
  lastEvent: RealtimeEvent | null;
  lastEventAt: number | null;
}

interface SharedStream {
  es: EventSource | null;
  refs: number;
  state: SSEConnectionState;
  eventListeners: Set<(event: RealtimeEvent) => void>;
  stateListeners: Set<(s: SSEConnectionState) => void>;
  // Reconnect bookkeeping. We take over reconnection from the native
  // EventSource (which retries on a fixed ~3s with NO jitter) so a
  // correlated drop — a control droplet dying, or a deploy closing every
  // stream — doesn't stampede the fleet with synchronized reconnects.
  attempt: number;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  // Liveness: timestamp of the last byte we observed (any event OR the
  // server's sse.ping). EventSource doesn't fire onerror for a SILENTLY
  // wedged socket (proxy idle-drop, sleep/wake, half-open TCP), so a
  // watchdog polls this and forces a reconnect once it goes stale —
  // otherwise the stream sits "connected" with no data until a manual
  // refresh (the symptom this whole mechanism fixes).
  lastActivityAt: number;
  watchdogTimer: ReturnType<typeof setInterval> | null;
}

const streams = new Map<string, SharedStream>();

// A deploy broadcasts system.reload to every tab at once; spread the hard
// refresh over this window so 50k tabs don't reload in the same second.
const RELOAD_JITTER_MS = 20_000;

function setStreamState(stream: SharedStream, s: SSEConnectionState): void {
  stream.state = s;
  for (const l of stream.stateListeners) l(s);
}

function scheduleReconnect(url: string, stream: SharedStream): void {
  if (stream.reconnectTimer || stream.refs <= 0) return;
  const delay = reconnectDelayMs(stream.attempt, Math.random());
  stream.attempt = Math.min(stream.attempt + 1, 16); // cap the exponent
  stream.reconnectTimer = setTimeout(() => {
    stream.reconnectTimer = null;
    if (stream.refs > 0) connectStream(url, stream);
  }, delay);
}

// Tear down the current socket and start the jittered backoff path. Shared by
// onerror (EventSource told us) and the watchdog (we inferred a silent wedge).
function dropAndReconnect(url: string, stream: SharedStream): void {
  try {
    stream.es?.close();
  } catch {
    /* already closed */
  }
  stream.es = null;
  setStreamState(stream, "connecting");
  scheduleReconnect(url, stream);
}

// Reconnect NOW (no backoff) if the stream looks dead — used on tab refocus /
// network regain, where the user is waiting and a wedged socket should recover
// instantly rather than on the next watchdog tick or backoff delay.
function reconnectNowIfStale(url: string, stream: SharedStream): void {
  if (stream.refs <= 0) return;
  const stale =
    !stream.es ||
    stream.state !== "connected" ||
    isStreamStalled(stream.lastActivityAt, Date.now(), SSE_STALL_MS);
  if (!stale) return;
  if (stream.reconnectTimer) {
    clearTimeout(stream.reconnectTimer);
    stream.reconnectTimer = null;
  }
  stream.attempt = 0; // user-visible wake — recover fast, no backoff
  stream.lastActivityAt = Date.now();
  try {
    stream.es?.close();
  } catch {
    /* ignore */
  }
  stream.es = null;
  connectStream(url, stream);
}

function startWatchdog(url: string, stream: SharedStream): void {
  if (stream.watchdogTimer) return;
  stream.watchdogTimer = setInterval(() => {
    // Only police a socket we believe is OPEN. If es is null we're already in
    // the backoff path — let it run; an "idle" reconnecting stream isn't stalled.
    if (!stream.es) return;
    if (isStreamStalled(stream.lastActivityAt, Date.now(), SSE_STALL_MS)) {
      console.warn("[useSSE] stream silent past stall window — forcing reconnect");
      stream.lastActivityAt = Date.now(); // don't re-fire every tick while it reconnects
      dropAndReconnect(url, stream);
    }
  }, SSE_WATCHDOG_INTERVAL_MS);
}

// One set of document/window listeners for the whole module: on tab refocus
// or network regain, kick every stale stream back to life immediately.
let globalWakeBound = false;
function ensureGlobalWakeListeners(): void {
  if (globalWakeBound) return;
  if (typeof document === "undefined" || typeof window === "undefined") return;
  globalWakeBound = true;
  const wakeAll = () => {
    for (const [url, stream] of streams) reconnectNowIfStale(url, stream);
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") wakeAll();
  });
  window.addEventListener("online", wakeAll);
}

function connectStream(url: string, stream: SharedStream): void {
  setStreamState(stream, "connecting");
  const es = new EventSource(url);
  stream.es = es;

  es.onopen = () => {
    stream.attempt = 0; // healthy again — reset backoff
    stream.lastActivityAt = Date.now(); // fresh socket — start the stall clock now
    setStreamState(stream, "connected");
  };
  es.onerror = () => {
    // Take over from native auto-reconnect: close this socket and schedule
    // our own jittered retry. Without this, every dropped stream reconnects
    // on the browser's fixed ~3s timer simultaneously.
    dropAndReconnect(url, stream);
  };
  es.onmessage = (msg) => {
    // ANY byte from the server counts as liveness — real events AND the
    // periodic sse.ping. This is what the watchdog reads to tell "quiet but
    // healthy" apart from "wedged".
    stream.lastActivityAt = Date.now();
    let parsed: RealtimeEvent;
    try {
      parsed = JSON.parse(msg.data) as RealtimeEvent;
    } catch (err) {
      console.warn("[useSSE] non-JSON event:", msg.data, err);
      return;
    }
    // Server keepalive heartbeat — its only job is to prove the stream is
    // alive (it bumped lastActivityAt above). Never forward to consumers.
    if (parsed.type === "sse.ping") {
      return;
    }
    // Visibility changed server-side — drop this stream and reopen so
    // the SSE route recomputes our subscription set on the open path.
    if (parsed.type === "sse.refresh") {
      es.close();
      stream.es = null;
      stream.attempt = 0; // deliberate per-user refresh, not a failure
      if (stream.refs > 0) connectStream(url, stream);
      return;
    }
    // Deploy completed and the operator (scripts/do/release.sh)
    // broadcast a force-reload on chan:broadcast. Compare against
    // our baked-in NEXT_PUBLIC_BUILD_ID; reload only if the
    // published buildId differs — a user who already booted on the
    // new bundle would otherwise refresh for nothing.
    if (parsed.type === "system.reload") {
      const myBuildId = process.env.NEXT_PUBLIC_BUILD_ID;
      const newBuildId = parsed.payload?.buildId;
      if (myBuildId && newBuildId && myBuildId !== newBuildId) {
        // Jitter the reload: a deploy broadcasts this to every tab at once,
        // so reloading immediately would stampede the fleet. Spread it.
        const delay = Math.random() * RELOAD_JITTER_MS;
        console.log(`[useSSE] build mismatch (my=${myBuildId}, new=${newBuildId}) — reloading in ${Math.round(delay)}ms`);
        if (typeof window !== "undefined") {
          setTimeout(() => window.location.reload(), delay);
        }
      }
      return;
    }
    for (const l of stream.eventListeners) l(parsed);
  };
}

function acquireStream(url: string): SharedStream {
  ensureGlobalWakeListeners();
  let stream = streams.get(url);
  if (!stream) {
    stream = {
      es: null,
      refs: 0,
      state: "idle",
      eventListeners: new Set(),
      stateListeners: new Set(),
      attempt: 0,
      reconnectTimer: null,
      lastActivityAt: Date.now(),
      watchdogTimer: null,
    };
    streams.set(url, stream);
    connectStream(url, stream);
    startWatchdog(url, stream);
  }
  stream.refs++;
  return stream;
}

function releaseStream(url: string, stream: SharedStream): void {
  stream.refs--;
  if (stream.refs <= 0) {
    if (stream.reconnectTimer) {
      clearTimeout(stream.reconnectTimer);
      stream.reconnectTimer = null;
    }
    if (stream.watchdogTimer) {
      clearInterval(stream.watchdogTimer);
      stream.watchdogTimer = null;
    }
    stream.es?.close();
    streams.delete(url);
  }
}

export function useSSE(
  url: string,
  onEvent?: (event: RealtimeEvent) => void
): UseSSEReturn {
  const [state, setState] = useState<SSEConnectionState>("idle");
  const [lastEvent, setLastEvent] = useState<RealtimeEvent | null>(null);
  const [lastEventAt, setLastEventAt] = useState<number | null>(null);
  // Use a ref so the latest callback is always used by the shared
  // listener without re-registering on every render.
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    const stream = acquireStream(url);
    const onStreamEvent = (event: RealtimeEvent) => {
      setLastEvent(event);
      setLastEventAt(Date.now());
      onEventRef.current?.(event);
    };
    const onStreamState = (s: SSEConnectionState) => setState(s);
    stream.eventListeners.add(onStreamEvent);
    stream.stateListeners.add(onStreamState);
    setState(stream.state); // adopt the shared stream's current state

    return () => {
      stream.eventListeners.delete(onStreamEvent);
      stream.stateListeners.delete(onStreamState);
      releaseStream(url, stream);
      setState("idle");
    };
  }, [url]);

  return { state, lastEvent, lastEventAt };
}
